import React, { useState, useEffect, useCallback, useRef } from "react";
import { Box, Tooltip } from "@chakra-ui/react";
import { FaFolderOpen } from "react-icons/fa";

/**
 * Reports Bubble Widget
 *
 * Floating button that opens the View Reports modal for the most recently
 * visited patient row. Stays disabled until the user has actually entered a
 * patient row (i.e. PatientPage has dispatched at least one
 * `patientReportsContext` event), then remains enabled across navigation by
 * caching the last context in localStorage.
 *
 * Click behavior (the bubble never navigates):
 *  - If we're already on the cached patient's page, dispatches
 *    `openPatientReportsModal` so PatientPage opens the modal via its
 *    richer `handleViewReports` flow.
 *  - Otherwise, dispatches `globalOpenReportsModal`, which is handled by
 *    `GlobalReportsModalHost` and pops the same modal in place over the
 *    user's current page.
 */
const STORAGE_KEY = "lastPatientReportsContext";

const readCachedContext = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && parsed.patientUuid) {
      return parsed;
    }
  } catch (_) {
    /* ignore corrupt storage */
  }
  return null;
};

const writeCachedContext = (ctx) => {
  try {
    if (!ctx || !ctx.patientUuid) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch (_) {
    /* ignore */
  }
};

const ReportsBubble = () => {
  const [context, setContext] = useState(() => readCachedContext());
  const lastDispatchKeyRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      const detail = e?.detail || {};
      const patientUuid = detail.patientUuid;
      if (!patientUuid) return;
      const next = {
        patientUuid,
        patientName: detail.patientName || "",
        reportsFolderPath: detail.reportsFolderPath || "",
      };
      const key = `${next.patientUuid}|${next.reportsFolderPath}|${next.patientName}`;
      if (lastDispatchKeyRef.current === key) return;
      lastDispatchKeyRef.current = key;
      setContext(next);
      writeCachedContext(next);
    };
    window.addEventListener("patientReportsContext", handler);
    return () =>
      window.removeEventListener("patientReportsContext", handler);
  }, []);

  const isEnabled = Boolean(context && context.patientUuid);

  const handleBubbleMouseDown = useCallback((e) => {
    e.stopPropagation();
  }, []);

  const handleClick = useCallback(
    (e) => {
      e.stopPropagation();
      if (!isEnabled) return;
      const targetUuid = context.patientUuid;
      const onSamePatientPage =
        typeof window !== "undefined" &&
        window.location.pathname === `/patient/${targetUuid}`;

      if (onSamePatientPage) {
        // Let PatientPage open the modal using its richer handleViewReports
        // (folder fallback, email-folder discovery, etc.).
        window.dispatchEvent(
          new CustomEvent("openPatientReportsModal", {
            detail: { patientUuid: targetUuid },
          }),
        );
      } else {
        // Pop the modal in place via the global host — never navigate away
        // from whatever page the user is currently on.
        window.dispatchEvent(
          new CustomEvent("globalOpenReportsModal", {
            detail: {
              patientUuid: targetUuid,
              patientName: context.patientName || "",
              reportsFolderPath: context.reportsFolderPath || "",
            },
          }),
        );
      }
    },
    [context, isEnabled],
  );

  const tooltipLabel = isEnabled
    ? context.patientName
      ? `View reports for ${context.patientName}`
      : "View reports for last patient"
    : "Open a patient first to view their reports";

  const bubbleSize = "50px";
  const bgColor = isEnabled ? "purple.600" : "gray.400";
  const hoverBg = isEnabled ? "purple.500" : "gray.400";

  return (
    <Tooltip label={tooltipLabel} placement="top" hasArrow>
      <Box
        position="relative"
        width={bubbleSize}
        height={bubbleSize}
        borderRadius="full"
        bg={bgColor}
        color="white"
        display="flex"
        alignItems="center"
        justifyContent="center"
        boxShadow="0 4px 20px rgba(0, 0, 0, 0.15)"
        transition="all 0.3s ease"
        cursor={isEnabled ? "pointer" : "not-allowed"}
        opacity={isEnabled ? 1 : 0.6}
        _hover={
          isEnabled
            ? {
                bg: hoverBg,
                transform: "scale(1.05)",
                boxShadow: "0 6px 25px rgba(0, 0, 0, 0.2)",
              }
            : {}
        }
        onClick={handleClick}
        onMouseDown={handleBubbleMouseDown}
        flexShrink={0}
        aria-disabled={!isEnabled}
        role="button"
      >
        <FaFolderOpen size={20} />
      </Box>
    </Tooltip>
  );
};

export default ReportsBubble;
